/**
 * HarnClient — fetch wrapper for lexruntime harn-service HTTP API.
 *
 * Two modes:
 * - gateway: via the openclaw gateway at :18789/harn/* with a Lexiun JWT.
 *            This is the production-matching path. Route through the
 *            runtime's HTTP base URL obtained from the instance token flow.
 * - direct:  via a Vite proxy at /harn-direct → http://127.0.0.1:3210,
 *            hitting the host-standalone harn-service process for harness
 *            development. No auth.
 */

import { ManifestSchema, type HarnessManifest } from "@lexiun/harn-sdk/manifest";
import type {
  HarnAnswer,
  HarnessManifestSummary,
  HarnHealth,
  HarnTaskRecord,
  HarnTaskResult,
  HarnTaskSummary,
  HarnWireFrame,
} from "../types/harn";

export type { HarnessManifest };

export class HarnApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`HarnApiError ${status}: ${body.slice(0, 200)}`);
    this.name = "HarnApiError";
  }
}

export type HarnTarget =
  | {
      kind: "gateway";
      runtimeBaseUrl: string;
      getToken: () => Promise<string | null>;
    }
  | {
      kind: "direct";
      baseUrl: string; // e.g., "/harn-direct" (Vite proxy)
    };

export interface ListTasksParams {
  status?: string;
  harnessId?: string;
  orgId?: string;
  clientId?: string;
  matterId?: string;
  caseId?: string;
  /** Filter by parent task id — returns the chain children of the given task. */
  parent?: string;
  limit?: number;
}

export interface RunHarnessBody {
  orgId: string;
  clientId?: string;
  matterId?: string;
  caseId?: string;
  input: unknown;
  timeoutMs?: number;
  model?: { provider?: string; id?: string };
  /**
   * Continuation payload when resuming an awaiting-user chain. The
   * server validates topology + answer schemas against the parent's
   * stored questions and atomically consumes the token before
   * spawning the child task.
   */
  resume?: {
    token: string;
    answers: HarnAnswer[];
  };
}

export class HarnClient {
  constructor(public readonly target: HarnTarget) {}

  get modeLabel(): "Gateway (runtime)" | "Direct (host)" {
    return this.target.kind === "gateway" ? "Gateway (runtime)" : "Direct (host)";
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const headers: Record<string, string> = { ...extraHeaders };
    if (body !== undefined) headers["Content-Type"] = "application/json";

    let url: string;
    if (this.target.kind === "gateway") {
      url = `${this.target.runtimeBaseUrl}/harn${path}`;
      const token = await this.target.getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } else {
      url = `${this.target.baseUrl}${path}`;
    }

    const res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const text = await res.text();
    if (!res.ok) {
      throw new HarnApiError(res.status, text);
    }
    // harn-service always returns JSON, but empty bodies are possible on some errors
    return text ? (JSON.parse(text) as T) : ({} as T);
  }

  // ── Health ─────────────────────────────────────────────────────

  async getHealth(): Promise<HarnHealth> {
    return this.request("GET", "/health");
  }

  // ── Harness catalogue ──────────────────────────────────────────

  async listHarnesses(): Promise<{ harnesses: HarnessManifestSummary[] }> {
    return this.request("GET", "/harnesses");
  }

  /**
   * Full manifest for one harness. The raw response is validated through
   * harn-sdk's `ManifestSchema` (v1→v2 agree on every field §19.1 reads)
   * so callers get a typed `HarnessManifest` rather than `unknown` — a
   * malformed manifest throws loudly here instead of rendering a broken
   * form downstream.
   */
  async getHarness(id: string): Promise<HarnessManifest> {
    const raw = await this.request<unknown>("GET", `/harnesses/${encodeURIComponent(id)}`);
    return ManifestSchema.parse(raw);
  }

  // ── Tasks ──────────────────────────────────────────────────────

  async listTasks(params?: ListTasksParams): Promise<{ tasks: HarnTaskSummary[] }> {
    const qs = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== "") qs.set(k, String(v));
      }
    }
    const q = qs.toString();
    return this.request("GET", `/tasks${q ? `?${q}` : ""}`);
  }

  async getTask(taskId: string): Promise<HarnTaskRecord> {
    return this.request("GET", `/tasks/${encodeURIComponent(taskId)}`);
  }

  async getTaskResult(taskId: string): Promise<HarnTaskResult> {
    return this.request("GET", `/tasks/${encodeURIComponent(taskId)}/result`);
  }

  async cancelTask(taskId: string): Promise<{ cancelled: true }> {
    return this.request("DELETE", `/tasks/${encodeURIComponent(taskId)}`);
  }

  /**
   * Start a harness task. `opts.triggerReason`, when set, is sent as the
   * `X-Admin-Trigger-Reason` header — the wire contract for §19.4's
   * admin-audit emission (backend reads it in a follow-up PR; the header
   * is forward-compatible and harmless to send today).
   *
   * The value is `encodeURIComponent`-encoded: HTTP header values are
   * ISO-8859-1 only, and an admin reason can contain Unicode (em-dash,
   * accents, emoji) which makes `fetch()` throw "String contains
   * non ISO-8859-1 code point" on a raw header. The §19.4 backend
   * `decodeURIComponent`s it back.
   */
  async runHarness(
    harnessId: string,
    body: RunHarnessBody,
    opts?: { triggerReason?: string },
  ): Promise<{ taskId: string; status: HarnTaskRecord["status"] }> {
    const extraHeaders =
      opts?.triggerReason !== undefined
        ? { "X-Admin-Trigger-Reason": encodeURIComponent(opts.triggerReason) }
        : undefined;
    return this.request(
      "POST",
      `/run/${encodeURIComponent(harnessId)}`,
      body,
      extraHeaders,
    );
  }

  // ── WebSocket frame streaming ──────────────────────────────────

  /**
   * Whether this client supports live WS frame streaming.
   * Direct mode always supports it (Vite proxy forwards ws://). Gateway mode
   * currently does not — the openclaw plugin proxy returns 501 on upgrade
   * (see lexruntime/openclaw/extensions/lexiun/src/harnesses/proxy.ts:76-86).
   */
  get streamingSupported(): boolean {
    return this.target.kind === "direct";
  }

  /**
   * Subscribe to live frames for a task. Returns an unsubscribe function.
   * Frames: progress, log, llm_stream, and terminal result/error.
   * The socket closes after the terminal frame.
   *
   * In direct mode the browser connects via the /harnws-direct Vite proxy
   * which forwards to ws://127.0.0.1:3210/ws/tasks/:id.
   *
   * In gateway mode this throws — the openclaw plugin proxy returns 501 on
   * upgrade (see lexruntime/openclaw/extensions/lexiun/src/harnesses/proxy.ts).
   */
  streamTask(
    taskId: string,
    onFrame: (frame: HarnWireFrame) => void,
    onClose?: (code: number, reason: string) => void,
    onError?: (err: Error) => void,
  ): () => void {
    if (this.target.kind !== "direct") {
      throw new Error(
        "WS streaming not supported in gateway mode — openclaw plugin proxy returns 501 on upgrade. Use direct mode or poll GET /tasks/:id.",
      );
    }
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/harnws-direct/ws/tasks/${encodeURIComponent(taskId)}`;
    const ws = new WebSocket(url);

    ws.addEventListener("message", (ev) => {
      try {
        const frame = JSON.parse(typeof ev.data === "string" ? ev.data : "") as HarnWireFrame;
        onFrame(frame);
      } catch (err) {
        // ignore malformed
      }
    });
    ws.addEventListener("close", (ev) => onClose?.(ev.code, ev.reason));
    ws.addEventListener("error", () => onError?.(new Error("WebSocket error")));

    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }
}
